import { defineStore } from 'pinia';
import axios from 'axios';

export const useClientsStore = defineStore('clients', {
	state: () => ({
		total_projects: 0,
		countries_list: [],
		currency_list: [],
		language_list: [],
		client_data: {},
		project_data: {},
		market_data: {},
		market_labels: [],
		competitors_data: [],
		country_valid: false,
		language_valid: false,
		country_language_valid: false,
		accounts: [
			{
				business_account_id: 0,
				business_account: '',
				max_keywords: 0,
				num_projects: 0,
				num_markets: 0,
				projects: [
					{
						project_id: 0,
						project_name: '',
						domain_url: '',
						brand_name: '',
						project_num_markets_text: '',
						project_num_markets: 0,
						num_markets_paused: 0,
						market_limits: 1,
						markets: [
							{
								market_id: 0,
								country_code: '',
								language_code: '',
								cond_url: '',
								competitor1_url: '',
								competitor2_url: '',
								competitor3_url: '',
								competitor4_url: '',
								competitor5_url: '',
							},
						],
					},
				],
			},
		],
	}),
	getters: {
		businessAccounts(state) {
			return state.accounts;
		},
		totalProjects(state) {
			return state.total_projects;
		},
		countryList(state) {
			return state.countries_list;
		},
		currencyList(state) {
			return state.currency_list;
		},
		languageList(state) {
			return state.language_list;
		},
		dataClient(state) {
			return state.client_data;
		},
		dataProject(state) {
			return state.project_data;
		},
		dataMarket(state) {
			return state.market_data;
		},
		checkedCountry(state) {
			return state.country_valid;
		},
		checkedLanguage(state) {
			return state.language_valid;
		},
		checkedCountryLanguage(state) {
			return state.country_language_valid;
		},
		dataCompetitors(state) {
			return state.competitors_data;
		},
		marketLabels(state) {
			return state.market_labels;
		}
	},
	actions: {
		async fetchData() {
			const response = await axios.get('vuejs/clients', { withCredentials: true });

			if (response.data.data.length === 0) {
				return false;
			}

			this.accounts = response.data.data;
			this.total_projects = response.data.total_projects;
		},
		async fetchCountries() {
			const response = await axios.get('vuejs/country_list', { withCredentials: true });
			this.countries_list = response.data.countries;
		},
		async fetchCurrency() {
			const response = await axios.get('vuejs/currency_list', { withCredentials: true });
			this.currency_list = response.data.currency;
		},
		async fetchLanguages() {
			const response = await axios.get('vuejs/language_list', { withCredentials: true });
			this.language_list = response.data.languages;
		},
		async createClient(body) {
			const response = await axios.post('vuejs/create_client', body, { withCredentials: true });
			return response.data;
		},
		async fetchClient(id) {
			const response = await axios.post('vuejs/get_client', { business_account_id: id }, { withCredentials: true });
			this.client_data = response.data.data;
		},
		async updateClientLimits(body) {
			const response = await axios.post('vuejs/update_client_limits', body, { withCredentials: true });
			return response.data;
		},
		async updateAdminData(body) {
			const response = await axios.post('vuejs/update_client_admin_data', body, { withCredentials: true });
			return response.data;
		},
		async createProject(body) {
			const response = await axios.post('vuejs/create_project', body, { withCredentials: true });
			return response.data;
		},
		async createMarket(marketData, competitors_list) {
            const response = await axios.post('vuejs/create_market', {
                'marketData': marketData,
                'competitors_list': competitors_list
            }, { withCredentials: true });
            return response.data;
        },
		async fetchProject(id) {
			const response = await axios.post('vuejs/get_project', { project_id: id }, { withCredentials: true });
			this.project_data = response.data.data;
		},
		async updateProject(body) {
			const response = await axios.post('vuejs/update_project', body, { withCredentials: true });
			return response.data;
		},
		async deleteProject(body) {
			const response = await axios.post('vuejs/delete_project', body, { withCredentials: true });
			return response.data;
		},
		async activateProject(id) {
			await axios.post('vuejs/activate_project', { project_id: id }, { withCredentials: true });
		},
		async fetchMarket(id) {
			const response = await axios.post('vuejs/get_market', { market_id: id }, { withCredentials: true });
			this.market_data = response.data.data;
		},
		async updateMarket(body) {
			const response = await axios.post('vuejs/update_market', body, { withCredentials: true });
			return response.data;
		},
		async deleteMarket(body) {
			const response = await axios.post('vuejs/delete_market', body, { withCredentials: true });
			return response.data;
		},
		async activateMarket(id) {
			await axios.post('vuejs/activate_market', { market_id: id }, { withCredentials: true });
		},
		async checkCountry(body) {
			const response = await axios.post('vuejs/validate_project_country', body, { withCredentials: true });
			this.country_valid = response.data.is_country_valid;
		},
		async checkLanguage(body) {
			const response = await axios.post('vuejs/validate_project_language', body, { withCredentials: true });
			this.language_valid = response.data.is_language_valid;
		},
		async checkCountryLanguage(body) {
			const response = await axios.post('vuejs/validate_project_country_language', body, { withCredentials: true });
			this.country_language_valid = response.data.is_country_language_valid;
		},
		async fetchCompetitors(marketId) {
            const response = await axios.post('vuejs/get_competitors', { market_id: marketId }, { withCredentials: true });
            this.competitors_data = response.data.competitors;
            this.market_labels = response.data.labels;
        },
        async addNewCompetitor(marketId, data) {
			const request = {
				'market_id': marketId,
				'color': data['color'],
				'competitor_url': data['competitor_url'],
				'label_id': data['label_id']
			}

			const response = await axios.post('vuejs/add_new_competitor', request, { withCredentials: true });
			return response.data;
        },
		async updateCompetitors(marketId, createdCompetitors, deletedCompetitors, updatedCompetitors) {
			if (createdCompetitors.length > 0 || deletedCompetitors.length > 0 || updatedCompetitors.length > 0) {
				const request = {
					'market_id': marketId,
					'created': createdCompetitors,
					'deleted': deletedCompetitors,
					'updated': updatedCompetitors
				}

				const response = await axios.post('vuejs/update_competitors', request, { withCredentials: true });
				return response.data.data;
			}
			return null;
		},
		async updateCompetitorsOrder(orderData) {
			const response = await axios.post('vuejs/update_competitors_order', { 'order': orderData }, { withCredentials: true });
			return response.data;
		},
		async updateCompetitorsSelected(selectedData) {
			const response = await axios.post('vuejs/update_competitors_selected', { 'selected': selectedData }, { withCredentials: true });
			return response.data;
		},
		async deleteCompetitor(id) {
			const response = await axios.post('vuejs/delete_competitor', { 'id': id }, { withCredentials: true });
			return response.data;
		},
		async changeCompetitorColor(color, id) {
			const response = await axios.post('vuejs/change_competitor_color', { 'color': color, 'id': id }, { withCredentials: true });
			return response.data;
		},
		async editCompetitor(data) {
			const request = {
				'competitor_id': data['id'],
				'color': data['color'],
				'competitor_url': data['competitor_url'],
				'label_id': data['label_id']
			}

			const response = await axios.post('vuejs/update_competitor', request, { withCredentials: true });
			return response.data;
		},
		async updateTypeProject(data) {
			const response = await axios.post('vuejs/update_type_project', {
				'data': data
			}, { withCredentials: true });
			return response.data;
		},
		async toggleMarketRecoType(marketId, recoTypeCatalogId, isEnabled) {
			const response = await axios.patch(
				`vuejs/market/${marketId}/reco-type-toggle`,
				{ reco_type_catalog_id: recoTypeCatalogId, is_enabled: isEnabled },
				{ withCredentials: true }
			);
			return response.data;
		},
	},
	persist: false,
});
